import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import { BOOKMAKER_ALIASES, isExcludedFromBestBook } from '../utils/constants';
import { getTeamLogoUrl, getBookLogoUrl, formatGameTime } from '../utils/formatters';

// Create reverse alias map (caesars -> williamhill_us)
const REVERSE_ALIASES = Object.entries(BOOKMAKER_ALIASES).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

const GamePanel = ({
  gameId,
  game,
  sport = 'ncaab',
  bookmakers = [],
  onClose,
  onOddsClick
}) => {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSpreads, setShowSpreads] = useState(true);
  const [showTotals, setShowTotals] = useState(false);
  const [showAlts, setShowAlts] = useState(true);
  const [showSingleBooks, setShowSingleBooks] = useState(false);
  const [showEdgesOnly, setShowEdgesOnly] = useState(false);

  // Ref-based tooltip for performance (no re-renders on hover)
  const tooltipRef = useRef(null);
  const tooltipDataRef = useRef(null);

  // Fetch game data when gameId or sport changes
  useEffect(() => {
    if (gameId) {
      fetchGameData();
    } else {
      setMarketData(null);
      setError(null);
    }
  }, [gameId, sport]);

  const fetchGameData = async () => {
    setLoading(true);
    setError(null);
    try {
      const sportPath = sport.toLowerCase();
      const response = await fetch(`/api/games/${sportPath}/${gameId}/odds/h2h?all=true`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setMarketData(data);
    } catch (err) {
      console.error('Error fetching game data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        hideTooltip();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const hideTooltip = useCallback(() => {
    if (tooltipRef.current) {
      tooltipRef.current.style.display = 'none';
    }
    tooltipDataRef.current = null;
  }, []);

  const formatOdds = (odds) => {
    if (odds === null || odds === undefined || odds === '') return '-';
    const numOdds = parseInt(odds, 10);
    if (isNaN(numOdds)) return '-';
    return numOdds > 0 ? `+${numOdds}` : `${numOdds}`;
  };

  const calculateImpliedProbability = (odds) => {
    const numericOdds = parseInt(odds, 10);
    if (isNaN(numericOdds)) return null;
    if (numericOdds > 0) {
      return (100 / (numericOdds + 100)) * 100;
    } else {
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  const handleOddsHover = useCallback((event, odds, book, bestBook, bestBookDiff, bestBookPrice = null) => {
    if (!odds || odds === '-') return;
    if (!tooltipRef.current) return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    const bookAlias = BOOKMAKER_ALIASES[book] || book;
    const reverseAlias = REVERSE_ALIASES[book] || book;
    const isBestBook = bestBook && bestBook !== 'multi' &&
      (book === bestBook || bookAlias === bestBook || reverseAlias === bestBook);
    let edge = (isBestBook && bestBookDiff) ? bestBookDiff * 100 : 0;

    // Excluded book tying or beating the eligible best — show its edge vs the
    // primary best so the corner caret has context.
    const isExcludedBook = isExcludedFromBestBook(book);
    let excludedNote = '';
    if (isExcludedBook && bestBookPrice !== null && bestBookPrice !== undefined &&
        ((odds > 0 && bestBookPrice > 0 && odds >= bestBookPrice) ||
         (odds < 0 && bestBookPrice < 0 && odds >= bestBookPrice))) {
      if (odds > bestBookPrice) {
        const exEdge = Math.abs((odds - bestBookPrice) / bestBookPrice) * 100;
        excludedNote = `<div style="color: #4ade80;">${exEdge.toFixed(2)}% edge over best book</div>`;
      } else {
        excludedNote = `<div style="color: #9CA3AF;">Tied with best book</div>`;
      }
    }

    const x = event.clientX;
    const y = event.clientY;

    tooltipDataRef.current = {
      odds,
      book,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2)
    };

    const tooltip = tooltipRef.current;
    tooltip.style.display = 'block';
    tooltip.style.left = `${x + 10}px`;
    tooltip.style.top = `${y + 10}px`;

    tooltip.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 4px;">${book}</div>
      <div>Odds: ${odds > 0 ? '+' : ''}${odds}</div>
      <div>Implied: ${impliedProb.toFixed(2)}%</div>
      ${edge > 0 ? `<div style="color: #4ade80;">Edge: ${edge.toFixed(2)}%</div>` : ''}
      ${excludedNote}
    `;
  }, []);

  const calculateAmericanOddsDiff = (bestOdds, secondOdds) => {
    if (bestOdds === null || bestOdds === undefined || secondOdds === null || secondOdds === undefined) {
      return 0;
    }
    if ((bestOdds > 0 && secondOdds > 0) || (bestOdds < 0 && secondOdds < 0)) {
      return Math.abs((bestOdds - secondOdds) / secondOdds);
    } else if (bestOdds > 0 && secondOdds < 0) {
      const bestDistance = bestOdds - 100;
      const secondDistance = secondOdds + 100;
      return Math.abs((bestDistance - secondDistance) / secondOdds);
    } else {
      const bestDistance = bestOdds + 100;
      const secondDistance = secondOdds - 100;
      return Math.abs((bestDistance - secondDistance) / secondOdds);
    }
  };

  const getTierStyle = (diffValue) => {
    if (diffValue >= 0.25) {
      return { backgroundColor: 'green', color: 'yellow' };
    } else if (diffValue >= 0.15) {
      return { backgroundColor: '#F59E0B', color: '#1E40AF' };
    } else if (diffValue >= 0.05) {
      return { backgroundColor: '#3B82F6', color: 'white' };
    }
    return null;
  };

  const getCellStyle = (odds, allOddsForSide, bestBookDiff = null, bestBook = null, currentBook = null, allOddsWithBooks = null) => {
    if (!odds || !allOddsForSide || allOddsForSide.length === 0) return {};
    const validOdds = allOddsForSide.filter(o => o !== null && o !== undefined);
    if (validOdds.length <= 1) return {};

    // Excluded books (e.g. circa, kalshi) can't be the primary best — compute
    // bestOdds from eligible (non-excluded) books only. If they tie or beat the
    // primary best they still get a secondary outlined highlight below.
    const eligibleEntries = (allOddsWithBooks || [])
      .filter(o => o.odds !== null && o.odds !== undefined && !isExcludedFromBestBook(o.book));
    const eligibleOdds = eligibleEntries.map(o => o.odds);
    const bestOdds = eligibleOdds.length > 0 ? Math.max(...eligibleOdds) : Math.max(...validOdds);
    const booksWithBest = (eligibleOdds.length > 0 ? eligibleOdds : validOdds).filter(o => o === bestOdds).length;
    const isExcludedBook = currentBook ? isExcludedFromBestBook(currentBook) : false;

    // Check if bet365 is the best book (only for NCAAB)
    const isBet365Best = bestBook === 'bet365' && sport === 'ncaab';

    if (!isExcludedBook && odds === bestOdds) {
      const style = { fontWeight: 'bold', borderRadius: '3px' };
      if (booksWithBest === 1 && bestBookDiff !== null && bestBookDiff !== undefined) {
        const diffValue = Math.abs(bestBookDiff);
        const tierStyle = getTierStyle(diffValue);
        if (tierStyle) {
          Object.assign(style, tierStyle);
        } else {
          Object.assign(style, { color: '#F59E0B' });
        }
      } else {
        Object.assign(style, { color: '#F59E0B' });
      }
      return style;
    }

    // Secondary highlight: excluded book whose odds tie or beat the eligible best.
    // Visually distinct from the primary (outlined, no background fill) so the
    // primary best stays unambiguous. Always render an outline — for sub-5% edges
    // we use amber so the cue stays visible.
    if (isExcludedBook && eligibleOdds.length > 0 && odds >= bestOdds) {
      let diffValue = 0;
      if (odds > bestOdds) {
        diffValue = Math.abs(calculateAmericanOddsDiff(odds, bestOdds));
      } else if (bestBookDiff !== null && bestBookDiff !== undefined) {
        diffValue = Math.abs(bestBookDiff);
      }
      let caretColor;
      if (diffValue >= 0.25) caretColor = 'green';
      else if (diffValue >= 0.15) caretColor = '#F59E0B';
      else if (diffValue >= 0.05) caretColor = '#3B82F6';
      else caretColor = '#9CA3AF'; // sub-5% / tie — neutral grey
      return {
        borderRadius: '3px',
        // Corner caret only — no bold, so the cell stays visually quieter than
        // the primary best (which keeps the bg fill + bold).
        backgroundImage: `linear-gradient(225deg, ${caretColor} 7px, transparent 7px)`,
        backgroundRepeat: 'no-repeat',
      };
    }

    // NCAAB special case: If bet365 is the best book, also apply tier coloring to next best book
    if (isBet365Best && allOddsWithBooks && currentBook) {
      const otherBooksOdds = [...allOddsWithBooks]
        .filter(o => o.odds !== null && o.odds !== undefined && o.book !== 'bet365')
        .sort((a, b) => b.odds - a.odds);
      if (otherBooksOdds.length >= 1) {
        const nextBestBook = otherBooksOdds[0].book;
        const nextBestOdds = otherBooksOdds[0].odds;
        if (currentBook === nextBestBook) {
          if (otherBooksOdds.length >= 2) {
            const thirdBestOdds = otherBooksOdds[1].odds;
            const diff = calculateAmericanOddsDiff(nextBestOdds, thirdBestOdds);
            const tierStyle = getTierStyle(diff);
            if (tierStyle) {
              return { fontWeight: 'bold', borderRadius: '3px', ...tierStyle };
            }
          }
        }
      }
    }

    return {};
  };

  // Helper to check if a record is a main line for at least one book
  const isMainLine = (record, marketType) => {
    for (const key of Object.keys(record)) {
      if (key.startsWith(`${marketType}_`) && key.endsWith('_isMain') && record[key] === true) {
        return true;
      }
    }
    for (const book of bookmakers) {
      const checkBook = (b) => {
        if (record[`${marketType}_${b}_isAlternate`] === false) return true;
        return Object.keys(record).some(key =>
          key.startsWith(`${marketType}_`) &&
          key.includes(`_${b}`) &&
          key.endsWith('_isAlternate') &&
          record[key] === false
        );
      };
      if (checkBook(book)) return true;
      const aliasBook = REVERSE_ALIASES[book];
      if (aliasBook && checkBook(aliasBook)) return true;
    }
    return false;
  };

  const processMarketData = () => {
    if (!marketData?.markets?.[0]) return { h2h: [], spreads: [], totals: [] };

    const container = marketData.markets[0];
    const isSoccer = sport === 'soccer';

    let h2h = container.h2h || [];
    let spreads = container.spreads || [];
    let totals = container.totals || [];

    // Non-soccer sports have no draw — drop h2h_3_way records so only h2h_binary is shown
    if (!isSoccer) {
      h2h = h2h.filter(record => record.tierKey !== 'h2h_3_way');
    }

    // For soccer, merge h2h_3_way and h2h_binary data
    if (isSoccer && h2h.length > 0) {
      const h2h3WayRecord = h2h.find(record => record.tierKey === 'h2h_3_way');
      const h2hBinaryRecord = h2h.find(record => record.tierKey === 'h2h_binary');

      if (h2h3WayRecord || h2hBinaryRecord) {
        const mergedRecord = { ...(h2h3WayRecord || h2hBinaryRecord) };

        if (h2h3WayRecord && h2hBinaryRecord) {
          const sides = ['away', 'home', 'draw'];
          Object.keys(h2hBinaryRecord).forEach(key => {
            const match = key.match(/^h2h_([a-z0-9_]+)_(away|home|draw)$/);
            if (match && !key.includes('bestBook') && !key.includes('nextBook') && !key.includes('isAlternate') && !key.includes('isAvailable')) {
              const bookName = match[1];
              const existsIn3Way = sides.some(side =>
                h2h3WayRecord[`h2h_${bookName}_${side}`] !== undefined
              );
              if (!existsIn3Way) {
                sides.forEach(side => {
                  const oddsKey = `h2h_${bookName}_${side}`;
                  const availKey = `h2h_${bookName}_${side}_isAvailable`;
                  if (h2hBinaryRecord[oddsKey] !== undefined) {
                    mergedRecord[oddsKey] = h2hBinaryRecord[oddsKey];
                  }
                  if (h2hBinaryRecord[availKey] !== undefined) {
                    mergedRecord[availKey] = h2hBinaryRecord[availKey];
                  }
                });
              }
            }
          });
        }

        h2h = [mergedRecord];
      }
    }

    // Filter out alternates if showAlts is disabled
    if (!showAlts) {
      h2h = h2h.filter(record => isMainLine(record, 'h2h'));
      spreads = spreads.filter(record => isMainLine(record, 'spreads'));
      totals = totals.filter(record => isMainLine(record, 'totals'));
    }

    return { h2h, spreads, totals };
  };

  const renderMarketTable = (records, marketType, title, sideFilter = null) => {
    if (!records || records.length === 0) return null;

    const isSoccer = sport === 'soccer';
    const sides = marketType === 'totals'
      ? ['over', 'under']
      : (marketType === 'h2h' && isSoccer)
        ? ['away', 'draw', 'home']
        : ['away', 'home'];
    const sideLabels = marketType === 'totals'
      ? { over: 'O', under: 'U' }
      : { away: game?.away || 'Away', home: game?.home || 'Home', draw: 'Draw' };

    const allSpreadCombinations = [
      { side: 'away', sign: 'plus', label: game?.away || 'Away', displaySign: '+' },
      { side: 'away', sign: 'minus', label: game?.away || 'Away', displaySign: '-' },
      { side: 'home', sign: 'plus', label: game?.home || 'Home', displaySign: '+' },
      { side: 'home', sign: 'minus', label: game?.home || 'Home', displaySign: '-' }
    ];
    const spreadCombinations = sideFilter
      ? allSpreadCombinations.filter(c => c.side === sideFilter)
      : allSpreadCombinations;
    const hideSideColumn = marketType === 'spreads' && !!sideFilter;

    const pointGroups = {};
    records.forEach(record => {
      const point = record.point ?? 'main';
      if (!pointGroups[point]) {
        pointGroups[point] = record;
      }
    });

    const sortedPoints = Object.keys(pointGroups).sort((a, b) => {
      if (a === 'main') return -1;
      if (b === 'main') return 1;
      return parseFloat(a) - parseFloat(b);
    });

    let rowCount = 0;
    const HEADER_INTERVAL = 20;

    const renderHeaderRow = (keyPrefix) => (
      <tr key={`${keyPrefix}-header`} style={{ backgroundColor: 'var(--bs-table-bg, #212529)' }}>
        {marketType !== 'h2h' && (
          <th style={{ width: '80px', minWidth: '80px' }}>
            {marketType === 'totals' ? 'Total' : 'Line'}
          </th>
        )}
        {marketType === 'spreads' && (
          <th style={{ width: '40px', minWidth: '40px' }}>+/-</th>
        )}
        {marketType !== 'totals' && !hideSideColumn && (
          <th style={{ width: '60px', minWidth: '60px' }}>Side</th>
        )}
        {bookmakers.map(book => (
          <th key={`${keyPrefix}-${book}`} style={{ width: '70px', minWidth: '70px' }}>
            <img
              src={getBookLogoUrl(book)}
              alt={book}
              style={{ height: '18px' }}
              title={book}
            />
          </th>
        ))}
      </tr>
    );

    return (
      <div className="mb-4">
        <h6 style={{ color: 'rgb(245, 158, 11)', fontWeight: 'bold', marginBottom: '10px' }}>
          {title}
        </h6>
        <div style={{ overflowX: 'auto' }}>
          <Table bordered size="sm" className="text-center" style={{ fontSize: '0.85rem' }}>
            <thead>
              {renderHeaderRow('thead')}
            </thead>
            <tbody>
              {sortedPoints.map(point => {
                const record = pointGroups[point];
                const pointDisplay = point === 'main' ? '-' : point;

                if (marketType === 'spreads') {
                  const getSpreadOddsForBook = (book, sign, side) => {
                    const key = `spreads_${sign}_${side}_${book}`;
                    const isMainKey = `${key}_isMain`;
                    const isAltKey = `${key}_isAlternate`;
                    const isAvailableKey = `${key}_isAvailable`;

                    let value = record[key];
                    let isMain = record[isMainKey] === true;
                    let isAlt = record[isAltKey] === true;
                    let isAvailable = record[isAvailableKey] !== false;

                    if (value === undefined) {
                      const aliasBook = REVERSE_ALIASES[book];
                      if (aliasBook) {
                        const aliasKey = `spreads_${sign}_${side}_${aliasBook}`;
                        value = record[aliasKey];
                        isMain = record[`${aliasKey}_isMain`] === true;
                        isAlt = record[`${aliasKey}_isAlternate`] === true;
                        isAvailable = record[`${aliasKey}_isAvailable`] !== false;
                      }
                    }

                    if (value !== undefined) {
                      return { odds: value, isMain, isAlt, isAvailable };
                    }
                    return null;
                  };

                  const rows = [];
                  let hasAnyEdge = false;

                  for (const combo of spreadCombinations) {
                    const booksWithOdds = bookmakers.filter(book => {
                      const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                      return result && result.odds !== undefined;
                    });

                    if (booksWithOdds.length > 0) {
                      if (!showSingleBooks && booksWithOdds.length === 1) continue;

                      const hasMainLine_combo = bookmakers.some(book => {
                        const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                        return result?.isMain === true;
                      });
                      if (!showAlts && !hasMainLine_combo) continue;

                      const oddsWithAvailability = booksWithOdds.map(book => {
                        const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                        return { odds: result?.odds, isAvailable: result?.isAvailable !== false };
                      }).filter(o => o.odds !== undefined && o.odds !== null);

                      let comboHasEdge = false;
                      if (oddsWithAvailability.length > 1) {
                        const bestOdds = Math.max(...oddsWithAvailability.map(o => o.odds));
                        const booksWithBest = oddsWithAvailability.filter(o => o.odds === bestOdds);
                        if (booksWithBest.length === 1 && booksWithBest[0].isAvailable) {
                          comboHasEdge = true;
                          hasAnyEdge = true;
                        }
                      }

                      if (showEdgesOnly && !comboHasEdge) continue;
                      rows.push(combo);
                    }
                  }

                  if (rows.length === 0) return null;

                  const shouldInsertHeader = rowCount > 0 && Math.floor((rowCount + rows.length - 1) / HEADER_INTERVAL) > Math.floor((rowCount - 1) / HEADER_INTERVAL);
                  const groupStartRow = rowCount;
                  rowCount += rows.length;

                  const dataRows = rows.map((combo, comboIdx) => {
                    const allOddsForCombo = bookmakers.map(book => {
                      const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                      return result?.odds;
                    }).filter(o => o !== undefined && o !== null);

                    return (
                      <tr key={`${point}-${combo.sign}-${combo.side}`}>
                        {comboIdx === 0 && (
                          <td
                            rowSpan={rows.length}
                            style={{
                              verticalAlign: 'middle',
                              fontWeight: 'bold',
                              backgroundColor: 'rgba(0,0,0,0.02)'
                            }}
                          >
                            {pointDisplay}
                          </td>
                        )}
                        <td style={{
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          color: combo.displaySign === '+' ? '#10B981' : '#EF4444'
                        }}>
                          {combo.displaySign}
                        </td>
                        {!hideSideColumn && (
                          <td style={{ fontWeight: '500' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                              <img
                                src={combo.side === 'away' ? (game?.awayLogo || getTeamLogoUrl(game?.away)) : (game?.homeLogo || getTeamLogoUrl(game?.home))}
                                alt={combo.label}
                                style={{ width: '16px', height: '16px' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <span style={{ fontSize: '0.75rem' }}>{combo.label}</span>
                            </div>
                          </td>
                        )}
                        {bookmakers.map(book => {
                          const result = getSpreadOddsForBook(book, combo.sign, combo.side);
                          const odds = result?.odds;
                          const isAvailable = result?.isAvailable !== false;
                          const bestBook = record[`spreads_bestBook_${combo.sign}_${combo.side}`];
                          const bestBookDiff = record[`spreads_bestBookDiff_${combo.sign}_${combo.side}`];
                          const bestBookPrice = record[`spreads_bestBookPrice_${combo.sign}_${combo.side}`];
                          const allOddsWithBooks = bookmakers.map(b => {
                            const r = getSpreadOddsForBook(b, combo.sign, combo.side);
                            return { book: b, odds: r?.odds };
                          }).filter(o => o.odds !== undefined && o.odds !== null);
                          const cellStyle = getCellStyle(odds, allOddsForCombo, bestBookDiff, bestBook, book, allOddsWithBooks);

                          return (
                            <td
                              key={book}
                              style={{ padding: '4px', opacity: !isAvailable ? 0.25 : undefined, cursor: odds ? 'pointer' : 'default' }}
                              onClick={() => odds && onOddsClick && onOddsClick(
                                gameId, null, combo.side === 'away' ? game?.away : game?.home,
                                'spreads', book, odds, game?.away, game?.home,
                                combo.side === 'away' ? game?.away : game?.home, null,
                                point === 'main' ? null : parseFloat(point),
                                combo.sign === 'plus' ? 'plus' : 'minus'
                              )}
                              onMouseEnter={(e) => odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff, bestBookPrice)}
                              onMouseLeave={() => hideTooltip()}
                            >
                              {odds !== undefined && odds !== null ? (
                                <div style={{ ...cellStyle, padding: '2px 4px' }}>
                                  {formatOdds(odds)}
                                </div>
                              ) : (
                                <span style={{ color: '#ccc' }}>-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  });

                  return shouldInsertHeader && groupStartRow >= HEADER_INTERVAL
                    ? [renderHeaderRow(`spreads-${groupStartRow}`), ...dataRows]
                    : dataRows;
                }

                // For h2h and totals
                const getOddsForBook = (book, side) => {
                  let value = record[`${marketType}_${book}_${side}`];
                  if (value !== undefined) return value;
                  value = record[`${marketType}_${side}_${book}`];
                  if (value !== undefined) return value;
                  const aliasBook = REVERSE_ALIASES[book];
                  if (aliasBook) {
                    value = record[`${marketType}_${aliasBook}_${side}`];
                    if (value !== undefined) return value;
                    value = record[`${marketType}_${side}_${aliasBook}`];
                    if (value !== undefined) return value;
                  }
                  return undefined;
                };

                const getIsAvailable = (book, side) => {
                  const key1 = `${marketType}_${book}_${side}_isAvailable`;
                  const key2 = `${marketType}_${side}_${book}_isAvailable`;
                  if (record[key1] === false || record[key2] === false) return false;
                  const aliasBook = REVERSE_ALIASES[book];
                  if (aliasBook) {
                    const aliasKey1 = `${marketType}_${aliasBook}_${side}_isAvailable`;
                    const aliasKey2 = `${marketType}_${side}_${aliasBook}_isAvailable`;
                    if (record[aliasKey1] === false || record[aliasKey2] === false) return false;
                  }
                  return true;
                };

                // Hide single-book totals unless filter is on
                if (!showSingleBooks && marketType === 'totals') {
                  const booksWithData = bookmakers.filter(book =>
                    getOddsForBook(book, 'over') !== undefined ||
                    getOddsForBook(book, 'under') !== undefined
                  );
                  if (booksWithData.length <= 1) return null;
                }

                const filteredSides = (showEdgesOnly && marketType !== 'h2h') ? sides.filter(side => {
                  const oddsWithAvailability = bookmakers.map(book => ({
                    odds: getOddsForBook(book, side),
                    isAvailable: getIsAvailable(book, side)
                  })).filter(o => o.odds !== undefined && o.odds !== null);
                  if (oddsWithAvailability.length > 1) {
                    const bestOdds = Math.max(...oddsWithAvailability.map(o => o.odds));
                    const booksWithBest = oddsWithAvailability.filter(o => o.odds === bestOdds);
                    return booksWithBest.length === 1 && booksWithBest[0].isAvailable;
                  }
                  return false;
                }) : sides;

                if (filteredSides.length === 0) return null;

                const shouldInsertHeader = rowCount > 0 && Math.floor((rowCount + filteredSides.length - 1) / HEADER_INTERVAL) > Math.floor((rowCount - 1) / HEADER_INTERVAL);
                const groupStartRow = rowCount;
                rowCount += filteredSides.length;

                const dataRows = filteredSides.map((side, sideIdx) => {
                  const allOddsForSide = bookmakers.map(book => getOddsForBook(book, side))
                    .filter(o => o !== undefined && o !== null);

                  return (
                    <tr key={`${point}-${side}`}>
                      {marketType === 'totals' ? (
                        <td style={{ fontWeight: 'bold', backgroundColor: 'rgba(0,0,0,0.02)' }}>
                          <span style={{ color: side === 'over' ? '#10B981' : '#EF4444', marginRight: '4px' }}>
                            {sideLabels[side]}
                          </span>
                          {pointDisplay}
                        </td>
                      ) : (
                        <>
                          {marketType !== 'h2h' && sideIdx === 0 && (
                            <td
                              rowSpan={filteredSides.length}
                              style={{
                                verticalAlign: 'middle',
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(0,0,0,0.02)'
                              }}
                            >
                              {pointDisplay}
                            </td>
                          )}
                          <td style={{ fontWeight: '500' }}>
                            {side === 'draw' ? (
                              <span style={{ fontSize: '0.75rem' }}>Draw</span>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <img
                                  src={side === 'away' ? (game?.awayLogo || getTeamLogoUrl(game?.away)) : (game?.homeLogo || getTeamLogoUrl(game?.home))}
                                  alt={sideLabels[side]}
                                  style={{ width: '16px', height: '16px' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                <span style={{ fontSize: '0.75rem' }}>{sideLabels[side]}</span>
                              </div>
                            )}
                          </td>
                        </>
                      )}
                      {bookmakers.map(book => {
                        const odds = getOddsForBook(book, side);
                        const isAvailable = getIsAvailable(book, side);
                        const bestBook = record[`${marketType}_bestBook_${side}`];
                        const bestBookDiff = record[`${marketType}_bestBookDiff_${side}`];
                        const bestBookPrice = record[`${marketType}_bestBookPrice_${side}`];
                        const allOddsWithBooks = bookmakers.map(b => ({
                          book: b,
                          odds: getOddsForBook(b, side)
                        })).filter(o => o.odds !== undefined && o.odds !== null);
                        const cellStyle = getCellStyle(odds, allOddsForSide, bestBookDiff, bestBook, book, allOddsWithBooks);

                        const teamForSide = side === 'away' ? game?.away
                          : side === 'home' ? game?.home
                          : side === 'draw' ? 'Draw' : side;

                        return (
                          <td
                            key={book}
                            style={{ padding: '4px', opacity: !isAvailable ? 0.25 : undefined, cursor: odds ? 'pointer' : 'default' }}
                            onClick={() => odds && onOddsClick && onOddsClick(
                              gameId, null, teamForSide,
                              (marketType === 'h2h' && isSoccer) ? 'h2h_3_way' : marketType, book, odds,
                              game?.away, game?.home, teamForSide, null,
                              (point === 'main' || marketType === 'h2h') ? null : parseFloat(point), side
                            )}
                            onMouseEnter={(e) => odds && handleOddsHover(e, odds, book, bestBook, bestBookDiff, bestBookPrice)}
                            onMouseLeave={() => hideTooltip()}
                          >
                            {odds !== undefined && odds !== null ? (
                              <div style={{ ...cellStyle, padding: '2px 4px' }}>
                                {formatOdds(odds)}
                              </div>
                            ) : (
                              <span style={{ color: '#ccc' }}>-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                });

                return shouldInsertHeader && groupStartRow >= HEADER_INTERVAL
                  ? [renderHeaderRow(`${marketType}-${groupStartRow}`), ...dataRows]
                  : dataRows;
              })}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  // Extract record summaries (NCAAB has structured records)
  const getRecordSummary = (records, type) => {
    if (!Array.isArray(records)) return null;
    const rec = records.find(r => r.name === type);
    return rec?.summary || null;
  };

  const markets = useMemo(() => processMarketData(), [marketData, showAlts, sport]);

  const awayOverall = getRecordSummary(game?.awayRecord, 'overall');
  const awayConf = getRecordSummary(game?.awayRecord, 'vs. Conf.');
  const homeOverall = getRecordSummary(game?.homeRecord, 'overall');
  const homeConf = getRecordSummary(game?.homeRecord, 'vs. Conf.');
  const hasRecords = awayOverall || awayConf || homeOverall || homeConf;
  const hasRankings = (game?.awayRank && game.awayRank <= 25) || (game?.homeRank && game.homeRank <= 25);

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #333', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
          {/* Away team */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <img
              src={game?.awayLogo || getTeamLogoUrl(game?.away)}
              alt={game?.away}
              style={{ width: '28px', height: '28px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <div style={{ color: '#fff', fontFamily: "'Oswald', sans-serif", fontSize: '0.9rem', fontWeight: '600', lineHeight: 1.2 }}>
                {game?.awayRank && game.awayRank <= 25 && (
                  <span style={{ color: '#F59E0B', marginRight: '4px' }}>#{game.awayRank}</span>
                )}
                {game?.away || 'Away'}
              </div>
              {(awayOverall || awayConf) && (
                <div style={{ fontSize: '0.7rem', color: '#9CA3AF', lineHeight: 1.2 }}>
                  {awayOverall}{awayConf ? ` (${awayConf} conf)` : ''}
                </div>
              )}
            </div>
          </div>

          <span style={{ color: '#6c757d', fontSize: '0.8rem' }}>@</span>

          {/* Home team */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <img
              src={game?.homeLogo || getTeamLogoUrl(game?.home)}
              alt={game?.home}
              style={{ width: '28px', height: '28px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <div style={{ color: '#fff', fontFamily: "'Oswald', sans-serif", fontSize: '0.9rem', fontWeight: '600', lineHeight: 1.2 }}>
                {game?.homeRank && game.homeRank <= 25 && (
                  <span style={{ color: '#F59E0B', marginRight: '4px' }}>#{game.homeRank}</span>
                )}
                {game?.home || 'Home'}
              </div>
              {(homeOverall || homeConf) && (
                <div style={{ fontSize: '0.7rem', color: '#9CA3AF', lineHeight: 1.2 }}>
                  {homeOverall}{homeConf ? ` (${homeConf} conf)` : ''}
                </div>
              )}
            </div>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
      </div>

      {/* Game info bar */}
      {(game?.venue || game?.network || game?.gameTime) && (() => {
        const venueStr = typeof game.venue === 'string' ? game.venue : '';
        const networkStr = typeof game.network === 'string'
          ? game.network
          : (game.network?.names ? game.network.names.join(', ') : '');
        return (
          <div style={{ padding: '4px 12px', borderBottom: '1px solid #333', fontSize: '0.7rem', color: '#6c757d', display: 'flex', gap: '12px', flexWrap: 'wrap', flexShrink: 0 }}>
            {venueStr && (
              <span>{venueStr}{game.venueCity ? `, ${game.venueCity}` : ''}{game.venueState ? `, ${game.venueState}` : ''}</span>
            )}
            {networkStr && <span>TV: {networkStr}</span>}
            {game.gameTime && <span>{formatGameTime(game.gameTime)}</span>}
          </div>
        );
      })()}

      {/* Content */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px' }}>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading odds...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-4 text-danger">
            <p>Error loading data: {error}</p>
          </div>
        )}

        {!loading && !error && marketData && (
          <>
            {renderMarketTable(markets.h2h, 'h2h', 'Moneyline')}

            <div className="d-flex justify-content-center align-items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
              <Button
                variant={showSpreads ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => { setShowSpreads(true); setShowTotals(false); }}
              >
                Spreads
              </Button>
              <Button
                variant={showTotals ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => { setShowTotals(true); setShowSpreads(false); }}
              >
                Totals
              </Button>
              <span style={{ borderLeft: '1px solid #6c757d', height: '24px', margin: '0 8px' }} />
              <Button
                variant={showAlts ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowAlts(!showAlts)}
                title="Show alternate lines in addition to main lines"
              >
                Show Alts
              </Button>
              <Button
                variant={showSingleBooks ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowSingleBooks(!showSingleBooks)}
                title="Include lines where only 1 book has odds"
              >
                Show Single Books
              </Button>
              <Button
                variant={showEdgesOnly ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowEdgesOnly(!showEdgesOnly)}
                title="Show all lines where there is 1 book with an edge"
              >
                Show Edges
              </Button>
            </div>

            {showSpreads && (
              <>
                {renderMarketTable(markets.spreads, 'spreads', `${game?.away || 'Away'} Spreads`, 'away')}
                {renderMarketTable(markets.spreads, 'spreads', `${game?.home || 'Home'} Spreads`, 'home')}
              </>
            )}
            {showTotals && renderMarketTable(markets.totals, 'totals', 'Totals')}
          </>
        )}

        {!loading && !error && !marketData && (
          <div className="text-center py-4 text-muted">
            <p>No odds data available for this game.</p>
          </div>
        )}
      </div>

      {/* Edge % Tooltip - ref-based for performance */}
      <div
        ref={tooltipRef}
        style={{
          display: 'none',
          position: 'fixed',
          backgroundColor: '#1f2937',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 9999,
          fontSize: '0.85rem',
          minWidth: '150px',
          pointerEvents: 'none'
        }}
      />
    </>
  );
};

export default GamePanel;
